import { cloneDeep } from 'lodash';
import uploadTaskManager from '@/renderer/scripts/utils/upload';
import downloadTaskManager from '@/renderer/scripts/utils/download';
import user from '@/renderer/scripts/utils/user';
import {
  TransUploadTask,
  TransDownloadTask,
  TaskStatus,
  TaskEvents,
  TableName,
  TaskType,
  TransSubTask,
  TaskInDB,
} from './types';
import taskCache from './taskCache';
import taskDB from './taskDB';

/**
 * 实现步骤
 * - 监听入库
 * 1、监听单个任务的 update
 * 2、更新单个任务的状态到db
 *
 * - 恢复本地记录
 * 1、重新打开客户端时从db里取出数据并更新record
 * 2、取出running的task 自动恢复running
 *
 * 数据库存储结构
 * {
 *  taskId: '1685602573378680.8584615861195',
 *  orgId: 1,
 *  userId: 1,
 *  taskInfo: {} // TransUploadTask | TransUploadTask
 * }
 *
 * - 缓存更新机制
 * 背景：大量任务被监听到状态改变时需要触发更新db，导致db更新过于频繁，引起性能问题
 * 优化：1、监听到任务状态改变时，先计入缓存，缓存根据【到达某一批数量级】或【多长时间内没有更新过db】哪个条件先达成，触发一次批量更新
 *      2、缓存内部可控优先级，某些任务可以不需要等太久就触发更新
 *      3、监听应用退出时，取出所有缓存，立即执行一次更新
 */

/**
 * 上传任务的状态
 * 这里的状态列表不完全等于任务的状态。只是为了分组保存任务列表 保证在传输列表里出现在正确的位置
 * 比如[unReady, cancel, error]虽然代表finish，但是在传输列表里已完成列表只展示success，所以这些状态放在waiting列表里
 * */
const waitingStatusList = [
  TaskStatus.waiting,
  TaskStatus.paused,
  TaskStatus.unReady,
  TaskStatus.canceled,
  TaskStatus.error,
];
const runningStatusList = [TaskStatus.ready, TaskStatus.preparing, TaskStatus.checking, TaskStatus.running];
const finishStatusList = [TaskStatus.success, TaskStatus.finish];

class TransmissionDBManager {
  private userId = -1;

  private orgId = -1;

  constructor() {
    this.initUserInfo();
  }

  private initUserInfo() {
    const { organizationId, userId } = user.getUserInfo();
    this.userId = userId as number;
    this.orgId = organizationId as number;
  }

  // 由于db存储不支持func，所以存储之前先转为list对象，取出时再转为func
  private formatTaskFromDB(task: any): TaskInDB[] {
    const cloneTask = cloneDeep(task);
    return cloneTask.map((task: any) => {
      const { taskId, orgId, userId, taskInfo } = task;
      const copyTaskInfo = {
        ...taskInfo,
        getList: () => taskInfo.list,
        getListLength: () => taskInfo.list.length,
        getItem: (index: number) => taskInfo.list[index],
        sortListByStatus: () => {
          taskInfo.list.sort((a: { status: string }, b: { status: string }) => {
            if (a.status === 'error' && b.status === 'success') return -1;
            if (a.status === 'success' && b.status === 'error') return 1;
            return 0;
          });
        },
      };
      delete copyTaskInfo.list;
      return {
        taskId,
        orgId,
        userId,
        taskInfo: copyTaskInfo,
      };
    });
  }

  // 本地db恢复任务列表
  private async initTaskList() {
    const [uploadList, downloadList] = await Promise.all([
      taskDB.getList(TableName.uploadTable, { userId: this.userId, orgId: this.orgId }),
      taskDB.getList(TableName.downloadTable, { userId: this.userId, orgId: this.orgId }),
    ]);

    const uploadWaiting: TransUploadTask[] = [];
    const uploadRunning: TransUploadTask[] = [];
    const uploadFinish: TransUploadTask[] = [];
    const downloadWaiting: TransDownloadTask[] = [];
    const downloadRunning: TransDownloadTask[] = [];
    const downloadFinish: TransDownloadTask[] = [];
    this.formatTaskFromDB(uploadList).forEach((task: any) => {
      if (waitingStatusList.includes(task.taskInfo.status)) {
        uploadWaiting.push(task.taskInfo);
      } else if (runningStatusList.includes(task.taskInfo.status)) {
        uploadRunning.push(task.taskInfo);
      } else if (finishStatusList.includes(task.taskInfo.status)) {
        uploadFinish.push(task.taskInfo);
      }
    });
    this.formatTaskFromDB(downloadList).forEach((task: any) => {
      if (waitingStatusList.includes(task.taskInfo.status)) {
        downloadWaiting.push(task.taskInfo);
      } else if (runningStatusList.includes(task.taskInfo.status)) {
        downloadRunning.push(task.taskInfo);
      } else if (finishStatusList.includes(task.taskInfo.status)) {
        downloadFinish.push(task.taskInfo);
      }
    });
    uploadTaskManager.setList({
      waitingList: uploadWaiting,
      runningList: uploadRunning,
      finishList: uploadFinish,
    });
    downloadTaskManager.setList({
      waitingList: downloadWaiting,
      runningList: downloadRunning,
      finishList: downloadFinish,
    });

    // 上次running和waiting的任务记录其taskId
    const uploadRunningTaskIds = [
      ...uploadRunning.map(task => task.taskId),
      ...uploadWaiting.filter(task => task.status === TaskStatus.waiting).map(task => task.taskId),
    ];
    const downloadRunningTaskIds = [
      ...downloadRunning.map(task => task.taskId),
      ...downloadWaiting.filter(task => task.status === TaskStatus.waiting).map(task => task.taskId),
    ];

    // 所有任务先暂停
    uploadTaskManager.pause();
    downloadTaskManager.pause();

    setTimeout(() => {
      // 重启上次running、waiting的任务
      uploadRunningTaskIds.forEach(taskId => uploadTaskManager.restartTask(taskId));
      downloadRunningTaskIds.forEach(taskId => downloadTaskManager.restartTask(taskId));
    }, 1000);
  }

  /**
   * 任务更新
   * */
  private updateUploadTask({ task, status }: { task: TransUploadTask; status: TaskStatus }) {
    this.updateTask(TaskType.upload, { task, status });
  }

  private updateDownloadTask({ task, status }: { task: TransDownloadTask; status: TaskStatus }) {
    this.updateTask(TaskType.download, { task, status });
  }

  private updateTask(
    type: TaskType,
    { task, status }: { task: TransUploadTask | TransDownloadTask; status: TaskStatus },
  ) {
    if (window.disableTransDB) return;

    // 所有任务状态变化统一经过缓存处理
    taskCache.add({ task, status, type });
  }

  // 初始化打开数据库并恢复任务列表
  public async init() {
    this.initUserInfo();
    taskCache.initUserInfo();
    await taskDB.init();
    await this.initTaskList();
    uploadTaskManager.on(TaskEvents.taskUpdate, this.updateUploadTask.bind(this));
    downloadTaskManager.on(TaskEvents.taskUpdate, this.updateDownloadTask.bind(this));
  }

  // 退出登录时进入 关闭数据库 关闭监听
  public async clear(clearTable?: boolean) {
    uploadTaskManager.off(TaskEvents.taskUpdate, this.updateUploadTask);
    downloadTaskManager.off(TaskEvents.taskUpdate, this.updateDownloadTask);
    taskCache.clear(); // 清空缓存
    // 需要时清空该用户所有数据
    if (clearTable) {
      await taskDB.clearByUserId({ userId: this.userId });
    }
    taskDB.close();
  }

  // 主动调用，将缓存的任务立即更新到db
  public updateCacheImmediately() {
    taskCache.updateCacheImmediately();
  }
}

const transmissionDBManager = new TransmissionDBManager();

export default transmissionDBManager;
